/**
 * PullQuote — oversized customer testimonial or brand statement with a
 * decorative open-quote mark. High drama, minimal markup.
 *
 * Registered as: custom / pull-quote
 */
import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import type { SectionComponentProps } from "../../registry.js";

gsap.registerPlugin(ScrollTrigger);

interface PullQuoteProps {
  quote?: string;
  attribution?: string;
  role?: string;
  /** 1–5 star rating (optional) */
  stars?: number;
}

export function PullQuote({ props }: SectionComponentProps) {
  const {
    quote = "Add a compelling customer testimonial or brand statement here.",
    attribution,
    role,
    stars,
  } = props as PullQuoteProps;

  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: { trigger: el, start: "top 78%", toggleActions: "play none none none" },
        defaults: { ease: "power3.out" },
      });
      tl.fromTo(".pq-mark", { opacity: 0, scale: 0.7, x: -20 }, { opacity: 1, scale: 1, x: 0, duration: 0.7 })
        .fromTo(".pq-quote", { opacity: 0, y: 28, clipPath: "inset(0 0 100% 0)" },
          { opacity: 1, y: 0, clipPath: "inset(0 0 0% 0)", duration: 1, ease: "power4.out" }, "-=0.3")
        .fromTo(".pq-meta", { opacity: 0, y: 16 }, { opacity: 1, y: 0, duration: 0.5 }, "-=0.4");
    }, el);

    return () => ctx.revert();
  }, []);

  const clampedStars = stars ? Math.min(5, Math.max(1, Math.round(stars))) : null;

  return (
    <section ref={sectionRef} className="py-24 px-6 bg-background" id="quote-section">
      <div className="max-w-3xl mx-auto relative">
        {/* Decorative quote mark */}
        <div className="pq-mark absolute -top-4 -left-2 md:-left-10 select-none pointer-events-none" style={{ color: "var(--color-primary)", opacity: 1 }}>
          <svg viewBox="0 0 80 60" className="w-16 h-12 md:w-20 md:h-14" fill="currentColor" aria-hidden>
            <path d="M0 60V36C0 16.8 10.2 5 30.6 0L36 9C26.4 12 21 18 21 27H36V60H0zm44 0V36C44 16.8 54.2 5 74.6 0L80 9C70.4 12 65 18 65 27H80V60H44z" />
          </svg>
        </div>

        <blockquote className="pt-6">
          <p
            className="pq-quote text-2xl md:text-4xl font-bold text-foreground leading-relaxed mb-8"
            style={{ fontStyle: "italic" }}
          >
            "{quote}"
          </p>

          {(attribution || clampedStars) && (
            <footer className="pq-meta flex items-center gap-4 flex-wrap">
              {clampedStars && (
                <div className="flex gap-0.5" aria-label={`${clampedStars} out of 5 stars`}>
                  {Array.from({ length: 5 }, (_, i) => (
                    <svg key={i} className="w-4 h-4" viewBox="0 0 20 20" fill={i < clampedStars ? "var(--color-primary)" : "currentColor"} aria-hidden>
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" className={i < clampedStars ? "" : "text-muted-foreground/30"} />
                    </svg>
                  ))}
                </div>
              )}
              {attribution && (
                <div>
                  <span className="font-semibold text-sm text-foreground">{attribution}</span>
                  {role && (
                    <span className="text-muted-foreground text-sm ml-1.5">— {role}</span>
                  )}
                </div>
              )}
            </footer>
          )}
        </blockquote>
      </div>
    </section>
  );
}
